import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faChevronRight, faCircleCheck, faCircleExclamation, faFlagCheckered, faRightFromBracket } from '@fortawesome/free-solid-svg-icons';
import { SEOHelmet } from 'components/SEO';
import { PlayPrimaryButton } from '../../components/PlayPrimaryButton';
import type { TournamentState } from 'types/interfaces/play';
import { playPath } from 'utils/play/inviteUrl';
import { resolveAdSlot } from 'utils/play/adSlot';
import { useGetPlayNotificationsQuery } from 'store/services/play/playApi';
import { AdCard } from '../../components/AdCard';
import { formatClock } from '../../playFormat';
import { PlayLayout } from '../../layout/PlayLayout';
import { playerTabs } from '../../layout/playTabs';
import { useRoundClock } from '../../hooks/useRoundClock';
import { myMatchView, resultSentence, viewerStanding } from '../../matchView';
import { ordinal, tournamentStatusLabel } from '../../playFormat';
import { EventFacts } from '../Invite/InvitePage';
import { DropSheet, JudgeSheet } from './sheets';
import './PlayerHome.css';

interface Props {
  code: string;
  state: TournamentState;
}

/**
 * Decision 22, player lobby review: the player's home. Container order before
 * round 1 is the status card, the Decklist and Round alerts list, the
 * sponsored card, then the quiet Drop link. The match card takes over during a
 * round (paired, live, reported, bye). Ad slots (a), (b) and (c) are placed by
 * the slot resolver.
 */
const PlayerHomePage: React.FC<Props> = ({ code, state }) => {
  const navigate = useNavigate();
  const { tournament, viewer, currentRound } = state;
  const my = myMatchView(state.myMatch, viewer);
  const clock = useRoundClock(currentRound);
  const [sheet, setSheet] = useState<'drop' | 'judge' | null>(null);
  const standing = viewerStanding(state);
  const { data: prefs } = useGetPlayNotificationsQuery();
  const live = tournament.status === 'LIVE' && !!currentRound?.startedAt;
  const paired = tournament.status === 'PAIRED' || (!!currentRound && !currentRound.startedAt);
  const complete = tournament.status === 'COMPLETE';
  const canDrop = !viewer.dropped && !complete;
  const inRound = !!my && (paired || live) && !viewer.dropped;
  const lobby = !viewer.dropped && !complete && !inRound;
  const liveDone = live && !!my && (my.isBye || !!my.games);
  const adSlot = resolveAdSlot({
    adsEnabled: tournament.adsEnabled,
    screen: 'player-home',
    viewer: { dropped: viewer.dropped, status: tournament.status, liveUnreported: live && !!my && !my.isBye && !my.games && !my.disputed, liveDone },
  });
  const adEyebrow = my?.isBye ? 'Bye round' : `Match done · ${formatClock(Math.max(0, clock.remainingSeconds))} left in round`;
  const hasDecklist = !!state.players.find((p) => p.id === viewer.tournamentPlayerId)?.hasDecklist;
  const alertSummary = [prefs?.discordDm && 'Discord DM', prefs?.push && 'Browser push', prefs?.sms && 'SMS'].filter(Boolean).join(', ') || 'In-app only';

  const subtitle = viewer.dropped ? 'Dropped' : tournamentStatusLabel(tournament.status, currentRound?.number ?? null, currentRound?.totalRounds ?? tournament.swissRounds);

  return (
    <PlayLayout title={tournament.name} subtitle={subtitle} tabs={playerTabs(code, 'match', tournament.decklistRequired)}>
      <SEOHelmet title={`${tournament.name} - TradeMagic Play`} description="Your match, standings and decklist." noindex />
      <div className="play-column">
        {viewer.dropped && (
          <section className="play-card play-card--roomy" data-testid="player-dropped">
            <h1 className="play-title">You've dropped</h1>
            <p className="play-helper">Your earlier results stay on the standings. Ask the organizer if you want back in before the next round is paired.</p>
          </section>
        )}

        {!viewer.dropped && complete && (
          <section className="play-card play-card--roomy" data-testid="player-complete">
            <p className="play-eyebrow">Tournament complete</p>
            <h1 className="play-title">{standing ? `You finished ${ordinal(standing.rank)}` : 'Final standings are posted'}</h1>
            <p className="play-helper">Thanks for playing. Full standings and tiebreakers are on the Standings tab.</p>
          </section>
        )}

        {lobby && (
          <section className="play-hero-card" data-testid="player-lobby">
            <p className="play-hero-card__eyebrow">
              <FontAwesomeIcon icon={faCircleCheck} className="play-hero-card__check" />
              You're registered
            </p>
            <h1 className="play-hero-card__title">Waiting for round {(currentRound?.number ?? 0) + 1} pairings</h1>
            <p className="play-hero-card__body">We'll ping you the moment pairings go up. Keep this tab open or watch Discord.</p>
            <div className="play-hero-card__facts">
              <EventFacts tournament={tournament} showCount />
            </div>
          </section>
        )}

        {inRound && my && (
          <section className="play-card play-match-card" data-testid="player-match-card">
            <div className="play-match-card__top">
              <p className="play-eyebrow" style={{ margin: 0 }}>
                Round {currentRound?.number} of {currentRound?.totalRounds}
              </p>
              <span className="play-match-card__phase">{paired ? 'Starting soon' : clock.isPaused ? 'Clock paused' : 'In progress'}</span>
            </div>

            {my.isBye ? (
              <>
                <h1 className="play-title" style={{ marginTop: 12 }}>
                  You have a bye
                </h1>
                <p className="play-helper">You get a free match win (2-0). Relax until next round.</p>
              </>
            ) : (
              <>
                <div className="play-match-card__table">
                  <span className="play-match-card__table-label">Table</span>
                  <span className="play-match-card__table-num" data-testid="player-table">
                    {my.tableNumber ?? '–'}
                  </span>
                </div>
                <p className="play-match-card__opp">
                  vs <strong>{my.opponent?.username}</strong> <span className="play-match-card__opp-rec">· {my.opponent?.record} this event</span>
                </p>
              </>
            )}

            {paired && <p className="play-helper" style={{ marginTop: 10 }}>Head to your table. The clock starts when the organizer begins the round.</p>}

            {live && (
              <div className={`play-clock play-clock--${clock.tone}`} data-testid="player-clock">
                <span className="play-clock__time">{clock.text}</span>
                <span className="play-clock__caption">{clock.tone === 'overtime' ? 'Time called' : clock.isPaused ? 'Clock paused by organizer' : 'Time remaining'}</span>
              </div>
            )}

            {live && !my.isBye && (my.games || my.disputed) && (
              <div className={`play-result-banner ${my.disputed ? 'play-result-banner--disputed' : my.games && my.games[0] < my.games[1] ? 'play-result-banner--lost' : ''}`} data-testid="player-result">
                <p className="play-result-banner__title">{my.disputed ? 'Result disputed' : my.games ? resultSentence(my.games) : ''}</p>
                <p className="play-result-banner__sub">
                  {my.disputed ? 'Your opponent reported a different result. The organizer will settle it.' : 'Final unless your opponent disputes it.'}
                </p>
                {!my.disputed && (
                  <button type="button" className="play-text-button play-result-banner__change" onClick={() => navigate(playPath(code, 'report'))}>
                    Change
                  </button>
                )}
              </div>
            )}

            {live && !my.isBye && !my.games && (
              <div className="play-match-card__actions">
                <PlayPrimaryButton label={my.disputed ? 'Update my report' : 'Report result'} icon={faFlagCheckered} onClick={() => navigate(playPath(code, 'report'))} />
                <button type="button" className="play-secondary-button" onClick={() => setSheet('judge')} disabled={my.judgeOpen} data-testid="player-call-judge">
                  {my.judgeOpen ? 'Judge called' : 'Call a judge'}
                </button>
              </div>
            )}
            {live && !my.isBye && my.games && (
              <div className="play-match-card__actions">
                <button type="button" className="play-secondary-button" onClick={() => setSheet('judge')} disabled={my.judgeOpen}>
                  {my.judgeOpen ? 'Judge called' : 'Call a judge'}
                </button>
              </div>
            )}
          </section>
        )}

        {lobby && (
          <section className="play-card play-card--list" data-testid="player-home-list">
            {tournament.decklistRequired && (
              <Link to={playPath(code, 'deck')} className="play-list-row" data-testid="player-deck-row">
                <FontAwesomeIcon icon={hasDecklist ? faCircleCheck : faCircleExclamation} className={`play-list-row__icon ${hasDecklist ? 'play-list-row__icon--ok' : 'play-list-row__icon--warn'}`} />
                <span className="play-list-row__main">
                  <span className="play-list-row__label">Decklist</span>
                  <span className="play-list-row__sub">{hasDecklist ? 'Submitted' : 'Needed before round 1'}</span>
                </span>
                <FontAwesomeIcon icon={faChevronRight} className="play-list-row__chevron" />
              </Link>
            )}
            <Link to={`/play/me?from=${code}`} className="play-list-row" data-testid="player-alerts-row">
              <FontAwesomeIcon icon={faBell} className="play-list-row__icon" />
              <span className="play-list-row__main">
                <span className="play-list-row__label">Round alerts</span>
                <span className="play-list-row__sub">{alertSummary}</span>
              </span>
              <FontAwesomeIcon icon={faChevronRight} className="play-list-row__chevron" />
            </Link>
          </section>
        )}

        {adSlot && (adSlot === 'a' || adSlot === 'b' || adSlot === 'c') && (
          <AdCard slot={adSlot} code={code} formatLabel={tournament.format} eyebrow={adSlot === 'b' ? adEyebrow : undefined} />
        )}

        {!viewer.dropped && state.rounds.length > 0 && (
          <div className="play-summary-row" data-testid="player-summary">
            <div className="play-summary-cell">
              <span className="play-summary-cell__label">Record</span>
              <span className="play-summary-cell__value">{standing ? `${standing.wins}-${standing.losses}-${standing.draws}` : '0-0-0'}</span>
            </div>
            <div className="play-summary-cell">
              <span className="play-summary-cell__label">Standing</span>
              <span className="play-summary-cell__value">{standing ? ordinal(standing.rank) : '–'}</span>
            </div>
            <div className="play-summary-cell">
              <span className="play-summary-cell__label">Points</span>
              <span className="play-summary-cell__value">{standing ? standing.points : 0}</span>
            </div>
          </div>
        )}

        {canDrop && (
          <button type="button" className="play-quiet-link" onClick={() => setSheet('drop')} data-testid="player-drop-link">
            <FontAwesomeIcon icon={faRightFromBracket} />
            Drop from tournament
          </button>
        )}
      </div>

      {sheet === 'drop' && <DropSheet code={code} tournamentId={tournament.id} onClose={() => setSheet(null)} />}
      {sheet === 'judge' && my && <JudgeSheet code={code} matchId={my.match.id} tableNumber={my.tableNumber} onClose={() => setSheet(null)} />}
    </PlayLayout>
  );
};

export default PlayerHomePage;
